/**
 * travelApiError — typisierter Fehler für `travelApi.ts` (RK-18, E17).
 *
 * Ersetzt die bisherigen rohen `new Error(...)`-Würfe an ALLEN 17 throw-Sites von
 * `travelApi.ts` (architecture-reisekosten.md §14.8). `message` bleibt der TECHNISCHE
 * Roh-String (für Logs/Tests, unverändert lesbar/greifbar) — das UI liest AUSSCHLIESSLICH
 * `code` (+ `issues`/`calcError`/`unavailableExpenseIds`, je nach Code) über den EINEN
 * UI-Mapper `formatTravelError` (`utils/travel/travelErrorMessages.ts`, V23).
 *
 * Eigene Datei (statt in `travelApi.ts` selbst): sowohl `travelApi.ts` als auch der
 * UI-Mapper importieren diese Klasse — eine Definition in `travelApi.ts` würde einen
 * Zyklus `travelApi.ts` ↔ `travelErrorMessages.ts` erzeugen.
 *
 * @module travelApiError
 */

import type { TravelValidationIssue } from '../utils/travel/travelValidation';
import type { TravelCalcErrorCode } from '../utils/travel/travelAllowance';

/**
 * Fehler-Codes von `travelApi.ts` (architecture-reisekosten.md §14.3/§14.8). Genau EIN Code
 * je throw-Site — `linkedExpenseUnavailable` wird erst von `settleTravelTrip` ab RK-22
 * (linkedExpenses-Snapshot, E19) tatsächlich geworfen; der Code ist bereits Teil des
 * gemeinsamen Vokabulars, damit i18n (§14.9) und `formatTravelError` einmalig vollständig
 * stehen.
 */
export type TravelApiErrorCode =
  | 'notAuthenticated'
  | 'validationFailed'
  | 'notFound'
  | 'wrongStatus'
  | 'crossScope'
  | 'calcFailed'
  | 'snapshotInvalid'
  | 'cancelReasonRequired'
  | 'linkedExpenseUnavailable';

/** Optionale, code-abhängige Zusatzdaten von {@link TravelApiError}. */
export interface TravelApiErrorOptions {
  /** Bei `code === 'validationFailed'`: die strukturierten Validierungsprobleme. */
  issues?: TravelValidationIssue[];
  /** Bei `code === 'calcFailed'`: der Fehlercode des Berechnungskerns (mappt auf `settlement.errors.*`). */
  calcError?: TravelCalcErrorCode;
  /** Bei `code === 'linkedExpenseUnavailable'`: die IDs der nicht ladbaren/nicht-`'booked'`-Belege (E19). */
  unavailableExpenseIds?: string[];
}

export class TravelApiError extends Error {
  readonly code: TravelApiErrorCode;
  readonly issues?: TravelValidationIssue[];
  readonly calcError?: TravelCalcErrorCode;
  readonly unavailableExpenseIds?: string[];

  constructor(code: TravelApiErrorCode, message: string, options?: TravelApiErrorOptions) {
    super(message);
    // TS/ES5-Downlevel-Falle (tsconfig target: es5): `extends Error` verliert ohne diesen
    // Fix die Prototype-Chain, sodass `instanceof TravelApiError` nach der Transpilation
    // fehlschlagen kann (Muster `BankImportError`, utils/bankImport/types.ts).
    Object.setPrototypeOf(this, TravelApiError.prototype);
    this.name = 'TravelApiError';
    this.code = code;
    this.issues = options?.issues;
    this.calcError = options?.calcError;
    this.unavailableExpenseIds = options?.unavailableExpenseIds;
  }
}
